'use client';
/* eslint-disable import/no-extraneous-dependencies */
import { styled } from '@linaria/react';
import { memo, useEffect, useRef, useState } from 'react';

const SliderBox = styled.div`
  position: relative;
`;

const Container = styled.div<{ gap?: number }>`
  display: flex;
  overflow: hidden;
  gap: ${props => props.gap || 8}px;
`;

const LeftArrow = styled.button<{ buttonPadding?: number }>`
  cursor: pointer;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: ${({ buttonPadding }) => (buttonPadding ? buttonPadding : 0)}px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  box-shadow: 0 2px 6px 0 rgba(0, 65, 143, 0.1);
  border: solid 1px #e2e8f1;
  background-color: white;
  border-radius: 18px;

  img {
    width: 10px;
    height: 16px;
  }
`;

const RightArrow = styled.button<{ buttonPadding?: number }>`
  cursor: pointer;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: ${({ buttonPadding }) => (buttonPadding ? buttonPadding : 0)}px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  box-shadow: 0 2px 6px 0 rgba(0, 65, 143, 0.1);
  border: solid 1px #e2e8f1;
  background-color: white;
  border-radius: 18px;

  img {
    width: 10px;
    height: 16px;
  }
`;

/* eslint-disable-next-line */
export interface SliderProps {
  children: React.ReactNode;
  gap?: number;
  buttonPadding?: number;
}

export function Slider({ children, gap, buttonPadding }: SliderProps) {
  const [sliderIndex, setSliderIndex] = useState(0);
  const [arrowVisible, setArrowVisible] = useState(false);
  const [leftArrowVisible, setLeftArrowVisible] = useState(false);
  const [rightArrowVisible, setRightArrowVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleScroll = (index = 0) => {
    if (ref.current) {
      const { offsetWidth, scrollWidth } = ref.current;
      const scrollLeft = (ref.current.childNodes[index] as HTMLDivElement)?.offsetLeft;
      setArrowVisible(scrollWidth > offsetWidth);
      setLeftArrowVisible(scrollLeft > 0);
      setRightArrowVisible(scrollLeft + offsetWidth < scrollWidth);
    }
  };

  const onClickLeftArrow = () => {
    if (sliderIndex - 1 >= 0) {
      setSliderIndex(sliderIndex - 1);
      ref.current?.scrollTo({
        left: (ref.current?.childNodes[sliderIndex - 1] as HTMLDivElement)?.offsetLeft,
        behavior: 'smooth'
      });
      handleScroll(sliderIndex - 1);
    }
  };

  const onClickRightArrow = () => {
    if (ref.current && sliderIndex + 1 < ref.current.childNodes.length) {
      setSliderIndex(sliderIndex + 1);
      ref.current?.scrollTo({
        left: (ref.current?.childNodes[sliderIndex + 1] as HTMLDivElement)?.offsetLeft,
        behavior: 'smooth'
      });
      handleScroll(sliderIndex + 1);
    }
  };

  useEffect(() => {
    if (ref.current) handleScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current]);

  return (
    <SliderBox>
      {arrowVisible && leftArrowVisible && (
        <LeftArrow onClick={onClickLeftArrow} buttonPadding={buttonPadding}>
          <img src='/assets/icons/arrows/arrow-simple-prev.svg' alt='left-arrow' />
        </LeftArrow>
      )}
      <Container ref={ref} gap={gap}>
        {children}
      </Container>
      {arrowVisible && rightArrowVisible && (
        <RightArrow onClick={onClickRightArrow} buttonPadding={buttonPadding}>
          <img src='/assets/icons/arrows/arrow-simple-next.svg' alt='right-arrow' />
        </RightArrow>
      )}
    </SliderBox>
  );
}

export default memo(Slider);
